// adminportal/src/pages/Statistics.tsx
import React, { useState, useEffect } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import { apiUrl, transferMonitoringApiUrl } from '../config';
import {
  RefreshCw,
  Users,
  Activity,
  TrendingUp,
  Shield,
  MapPin,
  Smartphone,
  Monitor,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import SftpMonitoringChart from '../components/SftpMonitoringChart';
import Copyright from '../components/Copyright';

interface UserAnalytics {
  totalUsers: number;
  activeUsers: number;
  newUsersThisWeek: number;
  mfaEnabledUsers: number;
  mfaAdoptionRate: number;
  loginTrends: Array<{
    date: string;
    logins: number;
    failedLogins: number;
    uniqueUsers: number;
  }>;
  userActivity: Array<{
    userId: string;
    username: string;
    email: string;
    lastLogin: string;
    loginCount: number;
    mfaEnabled: boolean;
    role: string;
    companyName: string;
  }>;
  geographicData: Array<{
    country: string;
    region: string;
    loginCount: number;
    uniqueUsers: number;
  }>;
  deviceTypes: {
    mobile: number;
    desktop: number;
    tablet: number;
  };
  securityEvents: Array<{
    timestamp: string;
    eventType:
      | 'LOGIN_SUCCESS'
      | 'LOGIN_FAILED'
      | 'MFA_ENABLED'
      | 'PASSWORD_RESET'
      | 'SUSPICIOUS_ACTIVITY';
    userId: string;
    username: string;
    ipAddress: string;
    userAgent: string;
    details: string;
  }>;
  errorCounts: {
    failedLogins: number;
    incompleteTransfers: number;
    s3Errors: number;
    apiErrors: number;
    lastUpdated: string;
  };
  sftpMetrics?: {
    serverId: string;
    period: string;
    lastUpdated: string;
    data: Array<{
      timestamp: string;
      successfulTransfers: number;
      failedTransfers: number;
      totalBytes: number;
    }>;
  };
}

export default function Statistics() {
  const [analytics, setAnalytics] = useState<UserAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('7d');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch real data from available APIs
      const [usersRes, dashboardRes, securityRes, errorRes] = await Promise.allSettled([
        fetch(apiUrl('users')),
        fetch(apiUrl('dashboard-data')),
        fetch(apiUrl('security-events')),
        fetch(apiUrl('error-counts')),
      ]);

      const usersData =
        usersRes.status === 'fulfilled' && usersRes.value.ok
          ? await usersRes.value.json()
          : { users: [] };

      const dashboardData =
        dashboardRes.status === 'fulfilled' && dashboardRes.value.ok
          ? await dashboardRes.value.json()
          : {};

      const securityData =
        securityRes.status === 'fulfilled' && securityRes.value.ok
          ? await securityRes.value.json()
          : { events: [] };

      const errorData =
        errorRes.status === 'fulfilled' && errorRes.value.ok
          ? await errorRes.value.json()
          : { errorCounts: {} };

      setAnalytics({
        totalUsers: usersData.users?.length || 0,
        activeUsers: dashboardData.activeUsers || 0,
        newUsersThisWeek: 0, // Not tracked yet
        mfaEnabledUsers: 0, // Not tracked yet
        mfaAdoptionRate: 0, // Not tracked yet
        loginTrends: [], // Not tracked yet
        userActivity: usersData.users?.slice(0, 10) || [],
        geographicData: [], // Not tracked yet
        deviceTypes: { mobile: 0, desktop: 100, tablet: 0 }, // Not tracked yet
        securityEvents: securityData.events || [],
        errorCounts: dashboardData.errorCounts || errorData.errorCounts || {},
        sftpMetrics: dashboardData.sftpMetrics,
      });
      setLastRefresh(new Date());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (autoRefresh) {
      interval = setInterval(fetchAnalytics, 30000); // Refresh every 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, timeRange]);

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'LOGIN_SUCCESS':
        return <CheckCircle className='w-4 h-4 text-green-500' />;
      case 'LOGIN_FAILED':
        return <XCircle className='w-4 h-4 text-red-500' />;
      case 'MFA_ENABLED':
        return <Shield className='w-4 h-4 text-blue-500' />;
      case 'PASSWORD_RESET':
        return <RefreshCw className='w-4 h-4 text-yellow-500' />;
      case 'SUSPICIOUS_ACTIVITY':
        return <AlertTriangle className='w-4 h-4 text-orange-500' />;
      default:
        return <Activity className='w-4 h-4 text-gray-500' />;
    }
  };

  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'LOGIN_SUCCESS':
        return 'text-green-400 bg-green-500/10 border-green-500/20';
      case 'LOGIN_FAILED':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      case 'MFA_ENABLED':
        return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
      case 'PASSWORD_RESET':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
      case 'SUSPICIOUS_ACTIVITY':
        return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
      default:
        return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
    }
  };

  if (loading && !analytics) {
    return (
      <div className='min-h-screen bg-slate-900 p-6'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex items-center justify-center h-64'>
            <div className='text-white text-lg'>Loading analytics...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-screen bg-slate-900 p-6'>
        <div className='max-w-7xl mx-auto'>
          <div className='text-center'>
            <div className='text-red-400 text-lg mb-4'>Error loading analytics: {error}</div>
            <Button onClick={fetchAnalytics} className='bg-blue-600 hover:bg-blue-700'>
              <RefreshCw className='w-4 h-4 mr-2' />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-slate-900 p-6'>
      <div className='max-w-7xl mx-auto'>
        {/* Header */}
        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8'>
          <div>
            <h1 className='text-3xl font-bold text-white mb-2'>Statistics & Analytics</h1>
            <p className='text-slate-400'>User activity, security events, and system performance</p>
          </div>
          <div className='flex items-center gap-4 mt-4 sm:mt-0'>
            <div className='flex items-center gap-2'>
              <label className='text-sm text-slate-300'>Time Range:</label>
              <select
                value={timeRange}
                onChange={e => setTimeRange(e.target.value as '24h' | '7d' | '30d')}
                className='bg-slate-800 border border-slate-600 rounded px-3 py-1 text-white text-sm'
                aria-label='Select time range for statistics'
              >
                <option value='24h'>Last 24 Hours</option>
                <option value='7d'>Last 7 Days</option>
                <option value='30d'>Last 30 Days</option>
              </select>
            </div>
            <Button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`${autoRefresh ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-600 hover:bg-slate-700'}`}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto Refresh ON' : 'Auto Refresh OFF'}
            </Button>
            <Button onClick={fetchAnalytics} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {lastRefresh && (
          <div className='text-sm text-slate-400 mb-6'>
            Last updated: {lastRefresh.toLocaleString()}
          </div>
        )}

        {/* Key Metrics Cards */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          <Card className='p-6 border-blue-500/20 bg-blue-500/10'>
            <div className='flex items-center gap-4'>
              <div className='p-3 bg-blue-500/20 rounded-lg'>
                <Users className='w-8 h-8 text-blue-400' />
              </div>
              <div>
                <div className='text-3xl font-bold text-blue-400'>{analytics?.totalUsers || 0}</div>
                <div className='text-sm text-blue-300'>Total Users</div>
                <div className='text-xs text-slate-400'>
                  +{analytics?.newUsersThisWeek || 0} this week
                </div>
              </div>
            </div>
          </Card>

          <Card className='p-6 border-green-500/20 bg-green-500/10'>
            <div className='flex items-center gap-4'>
              <div className='p-3 bg-green-500/20 rounded-lg'>
                <Activity className='w-8 h-8 text-green-400' />
              </div>
              <div>
                <div className='text-3xl font-bold text-green-400'>
                  {analytics?.activeUsers || 0}
                </div>
                <div className='text-sm text-green-300'>Active Users</div>
                <div className='text-xs text-slate-400'>{timeRange} period</div>
              </div>
            </div>
          </Card>

          <Card className='p-6 border-purple-500/20 bg-purple-500/10'>
            <div className='flex items-center gap-4'>
              <div className='p-3 bg-purple-500/20 rounded-lg'>
                <Shield className='w-8 h-8 text-purple-400' />
              </div>
              <div>
                <div className='text-3xl font-bold text-purple-400'>
                  {analytics?.mfaEnabledUsers || 0}
                </div>
                <div className='text-sm text-purple-300'>MFA Enabled</div>
                <div className='text-xs text-slate-400'>
                  {analytics?.mfaAdoptionRate || 0}% adoption
                </div>
              </div>
            </div>
          </Card>

          <Card className='p-6 border-red-500/20 bg-red-500/10'>
            <div className='flex items-center gap-4'>
              <div className='p-3 bg-red-500/20 rounded-lg'>
                <AlertTriangle className='w-8 h-8 text-red-400' />
              </div>
              <div>
                <div className='text-3xl font-bold text-red-400'>
                  {analytics?.errorCounts.failedLogins || 0}
                </div>
                <div className='text-sm text-red-300'>Failed Logins</div>
                <div className='text-xs text-slate-400'>{timeRange} period</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Charts and Visualizations */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8'>
          {/* Login Trends Chart */}
          <Card className='p-6 border-slate-700'>
            <h3 className='text-lg font-semibold text-white mb-4'>Login Trends</h3>
            <div className='h-64 flex items-center justify-center text-slate-400'>
              <div className='text-center'>
                <TrendingUp className='w-12 h-12 mx-auto mb-2 opacity-50' />
                <div>Login trends chart will be implemented</div>
              </div>
            </div>
          </Card>

          {/* Device Types */}
          <Card className='p-6 border-slate-700'>
            <h3 className='text-lg font-semibold text-white mb-4'>Device Types</h3>
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <Monitor className='w-5 h-5 text-blue-400' />
                  <span className='text-slate-300'>Desktop</span>
                </div>
                <div className='text-white font-semibold'>
                  {analytics?.deviceTypes.desktop || 0}%
                </div>
              </div>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <Smartphone className='w-5 h-5 text-green-400' />
                  <span className='text-slate-300'>Mobile</span>
                </div>
                <div className='text-white font-semibold'>
                  {analytics?.deviceTypes.mobile || 0}%
                </div>
              </div>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <Monitor className='w-5 h-5 text-purple-400' />
                  <span className='text-slate-300'>Tablet</span>
                </div>
                <div className='text-white font-semibold'>
                  {analytics?.deviceTypes.tablet || 0}%
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* User Activity Trends */}
        <div className='mb-8'>
          <Card className='p-6 border-slate-700'>
            <h3 className='text-lg font-semibold text-white mb-4'>
              User Activity Trends ({timeRange})
            </h3>
            <div className='h-64 flex items-center justify-center text-slate-400'>
              <div className='text-center'>
                <Activity className='w-12 h-12 mx-auto mb-2 opacity-50' />
                <div>User activity trends chart will be implemented</div>
                <div className='text-sm mt-2'>
                  Shows login patterns, peak usage times, and user engagement
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Recent User Activity */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8'>
          <Card className='p-6 border-slate-700'>
            <h3 className='text-lg font-semibold text-white mb-4'>Recent User Activity</h3>
            <div className='space-y-3 max-h-96 overflow-y-auto'>
              {analytics?.userActivity?.slice(0, 10).map((user, index) => (
                <div
                  key={index}
                  className='flex items-center justify-between p-3 bg-slate-800/50 rounded-lg'
                >
                  <div className='flex items-center gap-3'>
                    <div className='w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center'>
                      <Users className='w-4 h-4 text-slate-400' />
                    </div>
                    <div>
                      <div className='text-white font-medium'>{user.username}</div>
                      <div className='text-sm text-slate-400'>{user.companyName}</div>
                    </div>
                  </div>
                  <div className='text-right'>
                    <div className='text-sm text-slate-300'>
                      {new Date(user.lastLogin).toLocaleDateString()}
                    </div>
                    <div className='text-xs text-slate-400'>{user.loginCount} logins</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Security Events */}
          <Card className='p-6 border-slate-700'>
            <h3 className='text-lg font-semibold text-white mb-4'>Recent Security Events</h3>
            <div className='space-y-3 max-h-96 overflow-y-auto'>
              {analytics?.securityEvents?.slice(0, 10).map((event, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${getEventColor(event.eventType)}`}
                >
                  <div className='flex items-center gap-3'>
                    {getEventIcon(event.eventType)}
                    <div className='flex-1'>
                      <div className='text-sm font-medium'>{event.eventType.replace('_', ' ')}</div>
                      <div className='text-xs opacity-75'>{event.username}</div>
                    </div>
                    <div className='text-xs opacity-75'>
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Geographic Data */}
        {analytics?.geographicData && analytics.geographicData.length > 0 && (
          <Card className='p-6 border-slate-700'>
            <h3 className='text-lg font-semibold text-white mb-4'>Geographic Distribution</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {analytics.geographicData.slice(0, 6).map((geo, index) => (
                <div key={index} className='p-4 bg-slate-800/50 rounded-lg'>
                  <div className='flex items-center gap-2 mb-2'>
                    <MapPin className='w-4 h-4 text-blue-400' />
                    <span className='text-white font-medium'>{geo.country}</span>
                  </div>
                  <div className='text-sm text-slate-400'>
                    {geo.region && `${geo.region}, `}
                    {geo.country}
                  </div>
                  <div className='text-xs text-slate-500 mt-1'>
                    {geo.loginCount} logins • {geo.uniqueUsers} users
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Copyright with Easter egg */}
      <Copyright className='mt-8' showEasterEgg={true} />
    </div>
  );
}
